import { useGetEventByIdQuery } from '@/store/api/eventService/eventServiceApi';
import { useGetBuildingByIdQuery } from '@/store/api/spaceConfigService/spaceConfigServiceApi';
import { spaceConfigServiceApi } from '@/store/api/spaceConfigService/spaceConfigServiceApi';
import { MOCK_EVENT_DETAILS } from '@/mocks/reviewPanelMockData';
import type { EventData } from '@/store/models/events/entities';
import type { Building } from '@/store/models/buildings';
import type { SpaceWithAvailability } from '@/store/models/spaces';
import type { EventId, BuildingId } from '@/store/models/ids';
import { normalizeEventState } from '@/types/events.types';

export interface UseEventDetailsOptions {
  skipSpaces?: boolean;
}

export interface UseEventDetailsResult {
  event: EventData | undefined;
  building: Building | undefined;
  space: SpaceWithAvailability | undefined;
  /** All bookable spaces for the event's building, used to resolve details for multi-space bookings. */
  availableSpaces?: SpaceWithAvailability[];
  etag?: string;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

export function useEventDetails(
  eventId: EventId | null,
  options?: UseEventDetailsOptions
): UseEventDetailsResult {
  // Use `currentData`/`isFetching` (per-arg) rather than `data`/`isLoading`.
  // `data` retains the previously viewed event while a new one loads, which
  // caused the panel to briefly show stale details when switching events.
  const {
    currentData: eventData,
    isFetching: isEventFetching,
    isError,
    error,
    refetch,
  } = useGetEventByIdQuery(
    { eventId: eventId! },
    {
      skip: !eventId,
      // Never serve cached event details — every open of the panel must re-fetch.
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  );

  const rawEvent = eventData?.success ? eventData.data : undefined;
  const apiEvent = rawEvent
    ? { ...rawEvent, eventState: normalizeEventState(rawEvent.eventState) }
    : undefined;

  // Fallback to mock data when API returns error (for mock eventIds like evt-p001)
  const mockEvent = !apiEvent && eventId ? MOCK_EVENT_DETAILS[eventId as string] : undefined;
  const event = apiEvent ?? mockEvent;

  const buildingId = event?.buildingId;
  const { currentData: buildingData, isFetching: isBuildingFetching } = useGetBuildingByIdQuery(
    buildingId as BuildingId,
    {
      skip: !buildingId,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  );
  const building = buildingData?.data;

  const skipSpaces = options?.skipSpaces ?? false;
  const { currentData: spacesData, isFetching: isSpacesFetching } =
    spaceConfigServiceApi.useGetSpacesQuery(
      buildingId ? { buildingId, pageSize: 200 } : undefined,
      {
        skip: !buildingId || skipSpaces,
        refetchOnMountOrArgChange: true,
        refetchOnReconnect: true,
      }
    );
  const spaceId = event?.spaces?.[0]?.spaceId ?? event?.spaceId;
  const space = spacesData?.data?.find((s) => s.spaceId === spaceId);

  // Show the skeleton whenever data for the *current* selection isn't ready yet.
  // A background refetch of the same event keeps its resolved data, so this
  // stays false during in-place refreshes (e.g. after a planner save).
  const isEventPending = !apiEvent && isEventFetching;
  const isBuildingPending = !!buildingId && !building && isBuildingFetching;
  const isSpacesPending = !!buildingId && !skipSpaces && !spacesData && isSpacesFetching;
  const isFullyLoading = apiEvent
    ? isBuildingPending || isSpacesPending
    : isEventPending && !mockEvent;

  return {
    event,
    building,
    space,
    availableSpaces: spacesData?.data ?? [],
    etag: eventData?.etag,
    isLoading: isFullyLoading,
    isError: isError && !mockEvent,
    error,
    refetch: () => void refetch(),
  };
}
